import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { sizes } from '../utils/breakpoints'
import InfoCardListComponent from '../components/InfoCardListComponent'

const COUNT_DURATION = 1400

const Counter = ({ from, to, prefix = '', suffix = '' }) => {
  const [value, setValue] = useState(from)

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (prefersReducedMotion) {
      setValue(to)
      return
    }

    let frame
    let start

    const step = (timestamp) => {
      if (!start) start = timestamp
      const progress = Math.min((timestamp - start) / COUNT_DURATION, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(from + (to - from) * eased))
      if (progress < 1) frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [from, to])

  return (
    <>
      {prefix}
      {value}
      {suffix}
    </>
  )
}

const AboutUs = () => {
  const { t } = useTranslation()
  const stats = t('sobreNosotros.stats', { returnObjects: true })
  const pillars = t('sobreNosotros.pillars', { returnObjects: true })

  return (
    <Container>
      <Title>{t('sobreNosotros.title')}</Title>
      <Subtitle>{t('sobreNosotros.subtitle')}</Subtitle>

      <Stats>
        {stats.map((stat) => (
          <Stat key={stat.label}>
            <StatValue>
              <Counter
                from={stat.countFrom}
                to={stat.countTo}
                prefix={stat.prefix}
                suffix={stat.suffix}
              />
            </StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </Stat>
        ))}
      </Stats>

      <PillarsPanel>
        <Pillars>
          {pillars.map((pillar, index) => (
            <Pillar key={pillar.title}>
              <PillarNumber>{String(index + 1).padStart(2, '0')}</PillarNumber>
              <PillarTitle>{pillar.title}</PillarTitle>
              <PillarText>{pillar.text}</PillarText>
            </Pillar>
          ))}
        </Pillars>
      </PillarsPanel>

      <InfoCardListComponent />
    </Container>
  )
}

export default AboutUs

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  box-sizing: border-box;

  @media (max-width: ${sizes.mobile}px) {
    padding: 0 20px;
  }
`

const Title = styled.p`
  max-width: 620px;
  font-size: 36px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 43.2px;
  font-family: ${({ theme }) => theme.fonts.heading};
  text-align: center;

  @media (max-width: ${sizes.mobile}px) {
    max-width: 256px;
    font-size: 20px;
    line-height: 24px;
    font-family: ${({ theme }) => theme.fonts.headingAlt};
  }
`

const Subtitle = styled.p`
  max-width: 940px;
  font-size: 20px;
  line-height: 22.92px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 10px;
  font-family: ${({ theme }) => theme.fonts.body};
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: ${sizes.mobile}px) {
    max-width: 332px;
    line-height: 13.75px;
    font-size: 12px;
    margin-top: 5px;
  }
`

const Stats = styled.div`
  display: flex;
  justify-content: center;
  gap: 64px;
  margin-bottom: 56px;

  @media (max-width: ${sizes.mobile}px) {
    gap: 32px;
    margin-bottom: 32px;
  }
`

const Stat = styled.div`
  text-align: center;
`

const StatValue = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 40px;
  color: ${({ theme }) => theme.colors.text.accent};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 26px;
  }
`

const StatLabel = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 4px 0 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 11px;
  }
`

const PillarsPanel = styled.div`
  width: 100%;
  max-width: 1140px;
  box-sizing: border-box;
  background-color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'};
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 72px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 16px;
    border-radius: 14px;
    margin-bottom: 40px;
  }
`

const Pillars = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  width: 100%;

  @media (max-width: ${sizes.mobile}px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const Pillar = styled.div`
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 32px;
  text-align: left;

  @media (max-width: ${sizes.mobile}px) {
    padding: 20px;
  }
`

const PillarNumber = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.brand.green};
  background-color: ${({ theme }) => theme.colors.brand.dark};
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 8px;
  margin: 0 0 16px;
`

const PillarTitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 19px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 16px;
  }
`

const PillarText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 13px;
  }
`
